import type { ObjectId } from '@racemap/sdk/schema/base';
import type { UserOverview } from '@racemap/sdk/schema/user';
import type { ReactNode } from 'react';
import { copyIntoClipBoard } from 'src/components/utils/copyIntoClipBoard';
import { useIsAdmin } from 'src/lib/customHooks/useIsAdmin';
import { useStore } from '../store/reducers';
import {
  IconCopy,
  IconDollar,
  IconEmail,
  IconGhost,
  IconId,
  IconList,
  IconMailchimp,
  IconStripe,
  IconTracker,
  IconUser,
  IconWallet,
} from './BasicComponents/Icons';

/** Exactly one of `to`, `href` and `run` is set. */
export interface UserAction {
  key: string;
  title: string;
  /** Search terms the title does not carry. */
  keywords?: string;
  icon: ReactNode;
  /** A route inside the app. */
  to?: string;
  /** Leaves the app. A mailto opens in place, anything else in a new tab. */
  href?: string;
  run?: () => void;
}

/**
 * What can be done with one account, in sections. One list behind the UserLink menu and the
 * ⌘K account group, so the two cannot drift apart.
 */
export const useUserActions = (
  userId: ObjectId | string,
  user: UserOverview | null | undefined,
  // Scopes the tracker entry to this event as well as the user.
  eventId?: ObjectId | string,
): Array<Array<UserAction>> => {
  const isAdmin = useIsAdmin();
  const { impersonateUser } = useStore((s) => s.users.actions);

  if (user == null) return [];
  const id = userId.toString();

  const writeEmail: UserAction = {
    key: 'write-email',
    title: 'Write Email',
    keywords: 'mail send',
    icon: <IconEmail />,
    href: `mailto:${user.email}`,
  };

  // Nothing but the clipboard.
  const copyActions: Array<UserAction> = [
    {
      key: 'copy-email',
      // The address itself is the title: the link shows the name whenever there is one, so this
      // row is the only place the menu names the user's email.
      title: user.email,
      keywords: 'copy email address',
      icon: <IconCopy />,
      run: () => copyIntoClipBoard(user.email),
    },
    {
      key: 'copy-id',
      title: 'Copy User ID',
      icon: <IconId />,
      run: () => copyIntoClipBoard(id),
    },
  ];

  // Non-admins get the address and a way to write to it. Every other entry targets an /admin
  // page they cannot open.
  if (!isAdmin) return [[writeEmail], copyActions];

  const pageActions: Array<UserAction> = [
    { key: 'account', title: 'Account', icon: <IconUser />, to: `/admin/account/${id}` },
    {
      key: 'events',
      title: 'Events',
      icon: <IconList />,
      to: `/admin/events/manage?searchBy=USER&userId=${id}`,
    },
    {
      key: 'trackers',
      title: 'Tracker Management',
      keywords: 'devices',
      icon: <IconTracker />,
      to: `/admin/trackers?userIds=${id}${eventId != null ? `&eventIds=${eventId.toString()}` : ''}`,
    },
    // No date param: the billing view falls back to the current month.
    {
      key: 'billing',
      title: 'Billing',
      keywords: 'invoices',
      icon: <IconWallet />,
      to: `/admin/account/${id}/billing`,
    },
    {
      key: 'credits',
      title: 'Credits',
      icon: <IconDollar />,
      to: `/admin/user-credits?userId=${id}`,
    },
  ];

  const externalActions: Array<UserAction> = [
    ...(user.integrations?.stripeCustomerUrl != null
      ? [
          {
            key: 'stripe',
            title: 'To Stripe',
            icon: <IconStripe />,
            href: user.integrations.stripeCustomerUrl,
          },
        ]
      : []),
    ...(user.mailchimpContactUrl != null
      ? [
          {
            key: 'mailchimp',
            title: 'To Mailchimp',
            icon: <IconMailchimp />,
            href: user.mailchimpContactUrl,
          },
        ]
      : []),
    writeEmail,
  ];

  // Alone, because it is the only entry that changes who you are signed in as.
  const sessionActions: Array<UserAction> = [
    {
      key: 'impersonate',
      title: 'Impersonate This User',
      icon: <IconGhost />,
      run: () => impersonateUser(id),
    },
  ];

  return [pageActions, externalActions, copyActions, sessionActions];
};
